import { useEffect, type ReactNode } from 'react';
import clsx from 'clsx';

interface Props {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  footer?: ReactNode;
  children: ReactNode;
}

const SIZE = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  // Grows to the available viewport width (overlay padding is the only margin),
  // capped so it stays readable on very large monitors.
  '2xl': 'max-w-[1600px]',
};

export function Modal({ open, onClose, title, size = 'md', footer, children }: Props) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center bg-black/75 p-2 backdrop-blur-sm sm:p-5"
    >
      <div
        data-compact-form="true"
        className={clsx(
          'relative max-h-[95vh] w-full overflow-y-auto rounded-xl border border-[var(--border-color)] sm:max-h-[90vh] sm:rounded-2xl',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          'bg-[var(--modal-bg)]',
          SIZE[size],
        )}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--border-color)] bg-[var(--modal-bg)] px-3 py-2 sm:px-5 sm:py-3">
          <div className="min-w-0 flex-1 truncate pr-2 text-[13px] font-extrabold text-[var(--text-primary)] sm:text-base">{title}</div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-[var(--text-muted)] hover:bg-danger/10 hover:text-danger sm:h-8 sm:w-8"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="px-3 py-3 sm:px-5 sm:py-4">{children}</div>
        {footer && (
          <div
            className={
              'sticky bottom-0 z-10 flex justify-end gap-1 border-t border-[var(--border-color)] bg-[var(--modal-bg)] px-3 py-2 sm:gap-2.5 sm:px-5 sm:py-3 ' +
              // Mobile only: compact every child button so a 3–5 button row
              // fits on one line without wrapping.
              '[&_button]:!px-2.5 [&_button]:!py-1.5 [&_button]:!text-xs [&_button]:!gap-1 sm:[&_button]:!px-4 sm:[&_button]:!py-2 sm:[&_button]:!text-sm sm:[&_button]:!gap-1.5'
            }
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
